// Deal Submission spec §7 — origin-aware copy for the staff Deal Staging surfaces.
//
// Deal Staging serves TWO kinds of submission from one queue: a broker filling
// in the new-deal wizard, and a Buyer Consultant escalating a buyer submission
// into staging. The workflow is identical; the counterparty is not. On an
// escalation the liaison is talking to a Phoenix colleague, and every sentence
// that says "broker" is addressing the wrong person.
//
// 🔴 THIS IS A MAP, NOT A SWEEP. Some "broker" mentions are correct on BOTH
// origins and must not be touched:
//
//   • "View broker record" / the Broker column — an escalated deal really does
//     have a broker. The LISTING AGENT is the broker of record on it, their
//     Broker row exists, and the link resolves. Rewording it would break a
//     correct affordance.
//   • "Broker Liaison" — a Phoenix-internal role (C1). It is who REVIEWS the
//     submission, not who submitted it, and it is the same role either way.
//   • The queue search placeholder — the queue is mixed, so its chrome stays
//     origin-neutral.
//
// Everything that names the COUNTERPARTY is what belongs here. Resolve once per
// page (or once per row, in the mixed queue) and read fields off the result —
// scattering ternaries across ~40 call sites is how one gets missed and ships as
// a broker-voiced sentence in front of a liaison.
//
// ⚠️ The BrokerPortal branch is byte-identical to the strings that shipped
// before this file existed, and `stagingOriginCopy.test.ts` pins that. A diff
// there is a behaviour change to the broker flow, which this PR does not make.

import type { DealSubmissionOrigin } from "@/api/dealStaging";

export interface StagingOriginCopy {
  /** Queue column + review header value naming which door this came in. */
  originLabel: string;
  /** Longer gloss for the review header, where there is room for one. */
  originDescription: string;

  // --- Return ------------------------------------------------------------
  /** Button, modal title and confirm label. Kept as ONE value so the three
   *  can never drift out of agreement with each other. */
  returnAction: string;
  returnModalDescription: string;
  returnNotePlaceholder: string;
  returnNoteAriaLabel: string;
  /** Create Deal blocked-reason while the deal sits with the counterparty. */
  returnedBlockedReason: string;
  /** Trailing clause of the per-item flag hint. Composed from `returnAction`
   *  below so the quoted button label always matches the real button. */
  notifiedOnReturnHint: string;
  /** Label above a note that the counterparty will read. */
  facingNoteLabel: string;
  facingNoteItemLabel: string;

  // --- Return history ----------------------------------------------------
  /** " · 2 of 3 addressed by <this>" on the return-loop status line. */
  addressedByLabel: string;
  /** The per-comment resolved badge, minus the leading check. */
  addressedBadgeLabel: string;

  // --- Responses ---------------------------------------------------------
  responsesPanelTitle: string;
  responsesPanelDescription: string;
  responseFiledToast: string;
  responseDocumentTitle: string;
  /** Author fallback when a response carries no resolved display name. */
  responseAuthorFallback: string;
  /** Queue badge when unfiled responses are waiting on the liaison. */
  respondedBadge: string;
}

/** Who gets told, and how, when the liaison returns the deal. The broker gets
 *  an email; the specialist gets an in-app notification (they are staff and
 *  have no portal), which is what `DealSubmissionNotificationService`
 *  actually does on each branch. */
const NOTIFY_CLAUSE: Record<DealSubmissionOrigin, string> = {
  BrokerPortal: "The broker is emailed",
  BuyerSpecialistEscalation: "The consultant is notified",
};

function build(
  origin: DealSubmissionOrigin,
  copy: Omit<StagingOriginCopy, "notifiedOnReturnHint">,
): StagingOriginCopy {
  return {
    ...copy,
    // Curly quotes, matching the shipped string exactly.
    notifiedOnReturnHint:
      `${NOTIFY_CLAUSE[origin]} once you click “${copy.returnAction}”.`,
  };
}

const BROKER_PORTAL = build("BrokerPortal", {
  originLabel: "Broker portal",
  originDescription: "Submitted by a broker through the new-deal wizard.",

  returnAction: "Return to broker",
  returnModalDescription:
    "Sends the submission back to the broker — they're emailed once and it appears in their portal. " +
    "Any items you flagged for changes are included; add an overall note below.",
  returnNotePlaceholder: "Note to the broker (required)",
  returnNoteAriaLabel: "Note to the broker",
  returnedBlockedReason:
    "Returned to the broker — wait for resubmission before creating the deal",
  facingNoteLabel: "Broker-facing note",
  facingNoteItemLabel: "Broker-facing note for this item",

  addressedByLabel: "broker",
  addressedBadgeLabel: "Addressed by broker",

  responsesPanelTitle: "Broker responses",
  responsesPanelDescription:
    "Replies the broker posted to your return comments. " +
    "Filing a document lands it in the deal’s document set.",
  responseFiledToast: "Broker response document filed.",
  responseDocumentTitle: "Broker response document",
  responseAuthorFallback: "Broker",
  respondedBadge: "Broker responded",
});

const BUYER_SPECIALIST_ESCALATION = build("BuyerSpecialistEscalation", {
  originLabel: "Buyer escalation",
  originDescription: "Escalated from a buyer submission by a Buyer Consultant.",

  returnAction: "Return to consultant",
  // Deliberately does NOT say "appears in their portal": a Buyer Consultant is
  // Phoenix staff and has no external portal. The in-app notification is what
  // the server sends (Deal Submission spec §9.3), and it is what this sentence
  // describes.
  returnModalDescription:
    "Sends the submission back to the Buyer Consultant — they're notified in Phoenix with a link to the buyer submission. " +
    "Any items you flagged for changes are included; add an overall note below.",
  returnNotePlaceholder: "Note to the consultant (required)",
  returnNoteAriaLabel: "Note to the consultant",
  returnedBlockedReason:
    "Returned to the consultant — wait for resubmission before creating the deal",
  facingNoteLabel: "Consultant-facing note",
  facingNoteItemLabel: "Consultant-facing note for this item",

  addressedByLabel: "the consultant",
  addressedBadgeLabel: "Addressed by consultant",

  responsesPanelTitle: "Consultant responses",
  responsesPanelDescription:
    "Replies the consultant posted to your return comments. " +
    "Filing a document lands it in the deal’s document set.",
  responseFiledToast: "Consultant response document filed.",
  responseDocumentTitle: "Consultant response document",
  responseAuthorFallback: "Consultant",
  respondedBadge: "Consultant responded",
});

const BY_ORIGIN: Record<DealSubmissionOrigin, StagingOriginCopy> = {
  BrokerPortal: BROKER_PORTAL,
  BuyerSpecialistEscalation: BUYER_SPECIALIST_ESCALATION,
};

/**
 * The copy for one submission's origin. Falls back to the broker wording for an
 * unrecognised value rather than throwing: a queue that renders is worth more
 * than one that white-screens because the server grew a third origin, and the
 * broker branch is the safe default for every row that exists today.
 */
export function stagingCopyFor(
  origin: DealSubmissionOrigin | null | undefined,
): StagingOriginCopy {
  return (origin && BY_ORIGIN[origin]) || BROKER_PORTAL;
}

export function isEscalation(
  origin: DealSubmissionOrigin | null | undefined,
): boolean {
  return origin === "BuyerSpecialistEscalation";
}

// ---------------------------------------------------------------------------
// Fields the escalation deliberately leaves for the liaison
// ---------------------------------------------------------------------------

/**
 * Deal Submission spec §2.4 — the three business fields an escalation arrives with
 * EMPTY, on purpose, for the liaison to supply.
 *
 * 🔴 These are ASSIGNED WORK, not missing data, and the review surface has to
 * say so. A liaison who opens an unfamiliar deal type and finds three blank
 * business fields will reasonably conclude the submission is broken and bounce
 * it back to the specialist — who cannot fill them either, which is the whole
 * reason they are blank.
 *
 * Why each one is blank rather than guessed (see the escalation service and the
 * seed migration, which carry the same reasoning): every one of them is a field
 * where the buyer module holds a value that LOOKS right and means something
 * else. A listing carries a TRADE name, not a legal entity name. Its industry
 * is free text, not a NAICS code. Its cash flow is marketplace SDE, which is
 * neither NOI nor EBITDA. Copying any of them forward produces a loan file that
 * is confidently incorrect and surfaces at closing, where it is most expensive.
 * An empty field a human fills beats a populated field a human trusts.
 *
 * ⚠️ NO validation gate here. Import already refuses without the legal name and
 * NAICS code; staging deliberately does not, so a liaison can save partial work
 * mid-review. That separation is intentional — this is presentation only.
 *
 * Keyed on the schema's `mapsTo` token rather than the seed's field names: the
 * token is the stable, meaningful destination, and `parseField` preserves it.
 */
export interface LiaisonSuppliedField {
  /** Schema `mapsTo` token — matches `FormField.mapsTo`. */
  mapsTo: string;
  /** Short reason, phrased for a liaison who has not seen this deal type. */
  why: string;
}

export const ESCALATION_LIAISON_SUPPLIED_FIELDS: readonly LiaisonSuppliedField[] = [
  {
    mapsTo: "business.legalEntityName",
    why: "The listing carries a trade name, which is on the DBA field instead.",
  },
  {
    mapsTo: "business.naicsCode",
    why: "The listing's industry is free text, not a NAICS code.",
  },
  {
    mapsTo: "business.annualNoiPreliminary",
    why: "The listing's cash flow is marketplace SDE, which is neither NOI nor EBITDA.",
  },
];

const SUPPLIED_BY_TOKEN = new Map(
  ESCALATION_LIAISON_SUPPLIED_FIELDS.map((f) => [f.mapsTo, f]),
);

/**
 * The liaison-supplied entry for a form field, or undefined. Returns undefined
 * for every broker submission regardless of token, so a broker form whose
 * schema happens to map to the same destinations is untouched.
 */
export function liaisonSuppliedField(
  origin: DealSubmissionOrigin | null | undefined,
  mapsTo: string | null | undefined,
): LiaisonSuppliedField | undefined {
  if (!isEscalation(origin) || !mapsTo) return undefined;
  return SUPPLIED_BY_TOKEN.get(mapsTo);
}
